import { useEffect, useState } from "react";
import { User, BarChart3, Loader2, Upload, AlertTriangle, FileText, CheckCircle, Gauge } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { CommitteeReportsSection } from "../components/dashboard/CommitteeReportsSection";
import { AnalyticsTeaser } from "../components/dashboard/AnalyticsTeaser";
import EmailVerificationBanner from "../components/common/EmailVerificationBanner";
import api from "../services/api";

const TIER_DISPLAY = {
  free: "Free",
  basic: "Basic",
  pro: "Max",
};

const tierLabel = (tier) => TIER_DISPLAY[tier] || "Free";

const DownloadButton = () => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const handleDownload = async () => {
    setDownloading(true);
    setError("");

    try {
      const response = await api.get("/api/downloads/mec-downloader/windows");

      if (!response.data?.download_url) {
        throw new Error("No download URL received");
      }

      window.location.href = response.data.download_url;
      setDownloading(false);
    } catch (err) {
      console.error("Download error:", err);
      setDownloading(false);

      if (err.response?.status === 401) {
        setError("Session expired. Please log in again.");
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Failed to generate download link. Please try again.");
      }
    }
  };

  return (
    <div>
      <p style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '0.75rem', lineHeight: '1.4' }}>
        For users who want to collect reports in bulk before uploading.
      </p>
      <button
        className="btn-primary"
        onClick={handleDownload}
        disabled={downloading}
        style={{
          opacity: downloading ? 0.6 : 1,
          cursor: downloading ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.65rem 1.25rem',
          fontSize: '0.95rem',
          background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
          boxShadow: 'none'
        }}
      >
        {downloading ? (
          <>
            <Loader2 size={16} className="spin" />
            <span>Preparing download...</span>
          </>
        ) : (
          <span>Download for Windows</span>
        )}
      </button>
      <p style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.5rem', marginBottom: 0 }}>
        Windows only • ~90 MB
      </p>
      <p style={{
        fontSize: '0.75rem',
        color: '#9ca3af',
        marginTop: '0.5rem',
        marginBottom: 0
      }}>
        After download, Windows may ask you to confirm the file.
      </p>
      {error && (
        <div style={{
          marginTop: '0.75rem',
          padding: '0.5rem 0.75rem',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          fontSize: '0.85rem',
          color: '#dc2626'
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

export const Dashboard = () => {
  const { user, logout } = useAuth();
  const subscriptionTier = user?.tier || "free";

  // Quota info comes from the upload batch response OR can be polled
  // from /api/reports/quota if desired. We start with unknown values and
  // populate after the first batch upload of the session.
  const [uploadLimit, setUploadLimit] = useState(null);
  const [uploadsUsed, setUploadsUsed] = useState(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [error, setError] = useState("");
  const [showProcessingBanner, setShowProcessingBanner] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]);
  const [showAnalyticsTeaser, setShowAnalyticsTeaser] = useState(false);

  const MAX_FILES = 50;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  // Fetch current quota on mount so the usage card shows something useful
  // before the first upload of the session.
  useEffect(() => {
    let cancelled = false;
    const fetchQuota = async () => {
      try {
        const res = await api.get("/api/reports/quota");
        if (cancelled) return;
        const data = res.data || {};
        if (typeof data.upload_limit !== "undefined") setUploadLimit(data.upload_limit);
        if (typeof data.uploads_used === "number") setUploadsUsed(data.uploads_used);
      } catch (_err) {
        // Endpoint may not exist yet — silent fail. Card will show dashes.
      }
    };
    if (user) fetchQuota();
    return () => {
      cancelled = true;
    };
  }, [user]);

  /* ---------------- HANDLE MULTI-FILE SELECTION ---------------- */
  const handleFileSelect = (files) => {
    if (!files || files.length === 0) return;
    setError("");

    const newFiles = Array.from(files);

    const invalidFiles = newFiles.filter(f => !f.name.toLowerCase().endsWith('.pdf'));
    if (invalidFiles.length > 0) {
      setError(`Invalid files: ${invalidFiles.map(f => f.name).join(', ')}. Only PDF files are allowed.`);
      return;
    }

    const oversizedFiles = newFiles.filter(f => f.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setError(`Files too large: ${oversizedFiles.map(f => f.name).join(', ')}. Maximum 10MB per file.`);
      return;
    }

    const totalFiles = selectedFiles.length + newFiles.length;
    if (totalFiles > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files per batch. You have ${selectedFiles.length} selected and tried to add ${newFiles.length} more.`);
      return;
    }

    setSelectedFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (name) => {
    setSelectedFiles((prev) => prev.filter((f) => f.name !== name));
    setError("");
  };

  /* ---------------- BATCH UPLOAD ---------------- */
  const uploadAllFiles = async () => {
    if (selectedFiles.length === 0) {
      setError("Please select files first.");
      return;
    }

    setUploading(true);
    setUploadProgress(`Preparing to upload ${selectedFiles.length} file(s)...`);
    setError("");

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await api.post("/api/reports/upload/batch", formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const data = response.data;
      const { summary = {}, results = [] } = data;

      setUploadProgress("");
      setUploading(false);
      setSelectedFiles([]);

      // Update quota widgets from server response
      if (typeof summary.upload_limit !== "undefined") setUploadLimit(summary.upload_limit);
      if (typeof summary.uploads_used === "number") setUploadsUsed(summary.uploads_used);

      // Build a readable summary. Backend summary fields (new schema):
      //   total_files, successful, failed
      //   new_uploads_processed  — brand-new reports that counted toward quota
      //   duplicates             — dedup matches (FREE, no quota consumed)
      //   already_owned          — user already had access (no-op)
      //   uploads_used / uploads_remaining / upload_limit
      const newCount =
        summary.new_uploads_processed ??
        summary.new_reports ?? // legacy field name if backend hasn't been redeployed
        0;
      const dupCount = summary.duplicates ?? 0;
      const alreadyOwned = summary.already_owned ?? 0;

      let message = `Upload complete!\n\n`;
      message += `Successful: ${summary.successful ?? 0}/${summary.total_files ?? results.length}\n`;
      if (newCount > 0) {
        message += `New reports processed: ${newCount}\n`;
      }
      if (dupCount > 0) {
        message += `Duplicates (free — access granted): ${dupCount}\n`;
      }
      if (alreadyOwned > 0) {
        message += `Already in your library: ${alreadyOwned}\n`;
      }
      if ((summary.failed ?? 0) > 0) {
        message += `\nFailed: ${summary.failed}\n`;
        const failedFiles = results.filter(r => r.status === 'error');
        failedFiles.forEach(f => {
          message += `  - ${f.filename}: ${f.error}\n`;
        });
      }

      if (typeof summary.uploads_remaining !== "undefined" && summary.upload_limit !== null) {
        message += `\nUploads remaining this period: ${summary.uploads_remaining}`;
      } else if (summary.upload_limit === null) {
        message += `\nUnlimited uploads (Max tier)`;
      }

      alert(message);

      const successfulUploads = results
        .filter(r => r.status === 'success' || r.status === 'duplicate')
        .map(r => ({
          filename: r.filename,
          status: r.status,
          message: r.message,
          timestamp: new Date().toLocaleString()
        }))
        .slice(0, 5);

      setRecentUploads(prev => [...successfulUploads, ...prev].slice(0, 5));

      setShowProcessingBanner(true);
      setTimeout(() => setShowProcessingBanner(false), 30000);

    } catch (err) {
      console.error("Upload error:", err);

      if (err.response) {
        const data = err.response.data;
        const detail = data?.detail;
        if (err.response.status === 402) {
          // Quota exhausted — backend returns a structured detail payload.
          const msg =
            (detail && typeof detail === 'object' && detail.message) ||
            (typeof detail === 'string' && detail) ||
            "You've hit your upload limit for this period.";
          setError(msg);
        } else if (err.response.status === 401) {
          setError('Session expired. Please log in again.');
          setTimeout(() => logout(), 2000);
        } else if (detail) {
          setError(typeof detail === 'string' ? detail : detail.message || 'Upload failed');
        } else {
          setError('Upload failed. Please try again.');
        }
      } else {
        setError(`Upload failed: ${err.message}`);
      }

      setUploading(false);
      setUploadProgress("");
    }
  };

  const formatFileSize = (bytes) => {
    return (bytes / 1024 / 1024).toFixed(2) + " MB";
  };

  const uploadsRemaining =
    uploadLimit === null
      ? "∞"
      : uploadLimit != null && uploadsUsed != null
      ? Math.max(0, uploadLimit - uploadsUsed)
      : "—";

  const usageSubtext = (() => {
    if (subscriptionTier === "pro") return "Unlimited uploads";
    if (subscriptionTier === "basic") return "Rolling 365-day window";
    return "Lifetime trial — 3 free uploads";
  })();

  const isFreeOrBasic = subscriptionTier === "free" || subscriptionTier === "basic";
  const usageCardValue = (() => {
    if (uploadLimit === null) return "Unlimited";
    if (uploadLimit != null && uploadsUsed != null) {
      return `${uploadsUsed} / ${uploadLimit}`;
    }
    return "—";
  })();

  return (
    <>
      <Navbar />
      <main className="dashboard">
        <div className="dashboard-container">
          {/* HEADER */}
          <div className="dashboard-header">
            <div>
              <h1>Dashboard</h1>
              <p>Welcome back, {user?.full_name || user?.email || "User"}.</p>
            </div>
          </div>

          {/* STATS CARDS */}
          <div className="dashboard-stats">
            <div className="stat-card green">
              <div className="stat-icon"><Gauge size={24} /></div>
              <div>
                <div className="stat-label">Uploads Used</div>
                <div className="stat-value">{usageCardValue}</div>
                <p className="stat-subtext" style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  {usageSubtext}
                  {typeof uploadsRemaining === "number" && uploadLimit != null && (
                    <> · {uploadsRemaining} remaining</>
                  )}
                </p>
              </div>
            </div>

            <div className="stat-card yellow">
              <div className="stat-icon"><BarChart3 size={24} /></div>
              <div>
                <div className="stat-label">Subscription Tier</div>
                <div className="stat-value">{tierLabel(subscriptionTier)}</div>
                {isFreeOrBasic && (
                  <p className="stat-subtext" style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    <a href="/account/pricing" style={{ color: '#2563eb', textDecoration: 'underline' }}>
                      Upgrade to Max →
                    </a>
                  </p>
                )}
              </div>
            </div>

            <div className="stat-card email-card">
              <div className="stat-icon"><User size={24} /></div>
              <div>
                <div className="stat-label">Account Email</div>
                <div className="stat-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{user?.email || "loading..."}</span>
                  {user?.email_verified && (
                    <CheckCircle size={18} style={{ color: '#16a34a', flexShrink: 0 }} title="Email verified" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* EMAIL VERIFICATION BANNER */}
          <EmailVerificationBanner />

          {/* PROCESSING BANNER */}
          {showProcessingBanner && (
            <div style={{
              padding: '16px 20px',
              marginBottom: '20px',
              backgroundColor: '#eff6ff',
              border: '1px solid #3b82f6',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <svg style={{ width: '20px', height: '20px', animation: 'spin 1s linear infinite' }} viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <div>
                  <strong style={{ color: '#1e40af', fontSize: '1rem' }}>Reports are processing...</strong>
                  <p style={{ margin: 0, color: '#3b82f6', fontSize: '0.9rem' }}>
                    This usually takes 10-30 seconds. Click the Refresh button below when ready.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowProcessingBanner(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  fontSize: '1.5rem',
                  padding: '0 8px'
                }}
              >
                ×
              </button>
            </div>
          )}

          {/* UPLOAD SECTION */}
          <section className="dashboard-section compact-upload">
            <h3>Upload Campaign Reports</h3>
            <p className="subtext" style={{ marginBottom: '0.75rem' }}>
              Start by uploading campaign finance reports from the{' '}
              <a
                href="https://mec.mo.gov/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0066cc', textDecoration: 'underline' }}
              >
                Missouri Ethics Commission (MEC)
              </a>
            </p>
            <p className="subtext" style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 0 }}>
              Upload up to {MAX_FILES} PDF reports at once · Duplicate reports are free and don't consume your quota
            </p>

            {/* Error Message (from server or local validation) */}
            {error && (
              <div className="credit-warning error">
                <div className="credit-warning-message" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                  <span><strong>Error:</strong> {error}</span>
                </div>
                {isFreeOrBasic && (error.toLowerCase().includes('upload') || error.toLowerCase().includes('limit') || error.toLowerCase().includes('trial')) && (
                  <div className="credit-warning-actions">
                    <button
                      className="btn-outline small"
                      onClick={() => window.location.href = '/account/pricing'}
                      style={{
                        borderColor: '#dc2626',
                        color: '#dc2626'
                      }}
                    >
                      View Plans
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Upload Progress */}
            {uploadProgress && (
              <div style={{
                padding: '12px',
                marginBottom: '16px',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '6px',
                color: '#1e40af',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Loader2 size={16} className="spin" style={{ flexShrink: 0 }} />
                <span>{uploadProgress}</span>
              </div>
            )}

            <div
              className="upload-box"
              onClick={() => !uploading && document.getElementById("fileInput").click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (!uploading) {
                  handleFileSelect(e.dataTransfer.files);
                }
              }}
              style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}
            >
              {selectedFiles.length === 0 ? (
                <>
                  <Upload size={48} style={{ marginBottom: '0.75rem', opacity: 0.6 }} />
                  <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.25rem' }}>
                    Click or drag files to upload
                  </p>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    PDF files only, up to {MAX_FILES} files, 10MB max each
                  </p>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <FileText size={24} />
                    <p style={{ fontSize: '1rem', fontWeight: '500', margin: 0 }}>
                      {selectedFiles.length} file(s) selected
                    </p>
                  </div>
                  <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    Click to add more files (max {MAX_FILES} total)
                  </p>
                </>
              )}
            </div>

            <input
              type="file"
              id="fileInput"
              accept=".pdf"
              multiple={true}
              onChange={(e) => handleFileSelect(e.target.files)}
              style={{ display: 'none' }}
            />

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem', fontWeight: '600' }}>
                  Selected Files:
                </h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {selectedFiles.map((file, idx) => (
                    <li
                      key={idx}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.5rem',
                        marginBottom: '0.25rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '4px',
                        fontSize: '0.9rem'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FileText size={16} style={{ flexShrink: 0 }} />
                        <span>{file.name} ({formatFileSize(file.size)})</span>
                      </div>
                      <button
                        onClick={() => removeFile(file.name)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '1.2rem',
                          padding: '0 0.5rem'
                        }}
                        disabled={uploading}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Upload Button */}
                <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    className="btn-primary"
                    onClick={uploadAllFiles}
                    disabled={uploading || selectedFiles.length === 0}
                    style={{
                      opacity: uploading || selectedFiles.length === 0 ? 0.5 : 1,
                      cursor: uploading || selectedFiles.length === 0 ? 'not-allowed' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {uploading ? (
                      <>
                        <Loader2 size={16} className="spin" />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        <span>Upload {selectedFiles.length} File(s)</span>
                      </>
                    )}
                  </button>

                  <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                    Duplicates detected in your batch are free and don't count against your quota.
                  </span>
                </div>
              </div>
            )}
          </section>

          {/* RECENT UPLOADS SECTION */}
          {recentUploads.length > 0 && (
            <section className="dashboard-section" style={{ marginTop: '1.5rem' }}>
              <h3 style={{ marginBottom: '0.75rem' }}>Recent Uploads</h3>
              <p className="subtext" style={{ marginBottom: '1rem' }}>
                Your last {recentUploads.length} successfully uploaded report{recentUploads.length !== 1 ? 's' : ''}
              </p>
              <div style={{
                background: 'white',
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                padding: '1rem'
              }}>
                {recentUploads.map((upload, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.75rem',
                      borderBottom: idx < recentUploads.length - 1 ? '1px solid #e5e7eb' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                      <FileText size={20} style={{ color: '#2563eb', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: '#111827',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {upload.filename}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.1rem' }}>
                          {upload.timestamp}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      backgroundColor: upload.status === 'success' ? '#dcfce7' : '#fef3c7',
                      color: upload.status === 'success' ? '#166534' : '#92400e',
                      flexShrink: 0
                    }}>
                      {upload.status === 'success' ? '✓ New' : '⊚ Duplicate'}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* COMMITTEE REPORTS SECTION */}
          <CommitteeReportsSection />

          {/* TOOLS SECTION */}
          <section className="dashboard-section compact-upload" style={{ background: 'white', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
            <h3 style={{ marginBottom: '0.5rem' }}>Tools</h3>
            <div style={{
              padding: '1rem',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              background: '#f9fafb'
            }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', marginBottom: '0.35rem' }}>
                Bulk MEC Report Downloader (Windows)
              </h4>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '0.65rem', lineHeight: '1.5' }}>
                Optional helper to download MEC filings locally by committee name or MEC ID.
              </p>
              <DownloadButton />
            </div>
          </section>

          {/* ANALYTICS TEASER BUTTON FOR FREE/BASIC TIERS */}
          {isFreeOrBasic && (
            <div style={{
              marginTop: '2rem',
              padding: '1.5rem',
              background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)',
              borderRadius: '10px',
              border: '1px solid #bfdbfe',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📊</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                Unlock the Analytics Dashboard
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '1rem', lineHeight: '1.6' }}>
                The full analytics dashboard — donor trends, expense breakdowns, and cross-committee comparisons — is part of the Max plan ($20/year).
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  className="btn-primary"
                  onClick={() => setShowAnalyticsTeaser(true)}
                  style={{ padding: '0.75rem 1.5rem', fontSize: '1rem' }}
                >
                  Preview Analytics
                </button>
                <button
                  className="btn-outline"
                  onClick={() => window.location.href = '/account/pricing'}
                  style={{ padding: '0.75rem 1.5rem', fontSize: '1rem' }}
                >
                  Upgrade to Max
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* ANALYTICS TEASER MODAL */}
      <AnalyticsTeaser
        isOpen={showAnalyticsTeaser}
        onClose={() => setShowAnalyticsTeaser(false)}
      />
    </>
  );
};
